import {
  Dispatch,
  PayloadAction,
  ThunkAction,
  createSlice,
} from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { PLAN_VIEW } from 'modules/auth/constants/authConstants';
import * as PlanService from 'modules/auth/services/PlanService';
import { handleErrorCode } from 'modules/auth/util/error';
import { addMessage } from 'modules/chatbot/slices/chatbotSlice';
import {
  ChatMessage,
  MessageStatus,
} from 'modules/chatbot/types/chatbot.types';
import { Sender } from 'modules/chatbot/enums/Sender';
import {
  ID_CARD_LOGIN_SYSTEM_MESSAGE,
  ID_CARD_LOGOUT_SYSTEM_MESSAGE,
} from 'modules/chatbot/constants/constants';

interface IDCardState {
  /**
   * Keeps track of the currently selected dependent or subscriber's individual id.
   */
  currentMemberId: string;
  isLoggedIn: boolean;
  currentEmployee: any | null;
  view: string;
  electionData: any | null;
  publishedPlans: any | null;
  inProgress: boolean;
  /**
   * An array that keeps track of the employee's election data
   */
  electionMDVIdCardData: any | null;
  /**
   * Keeps track of the dependents id cards data as individualId-electionArray pairs
   */
  dependentMDVIdCardData?: Record<string, any[]>;
  employerData: any | null;
  currentPlanYear: any;
  /**
   * Plan years of the cards on screen by id: a staggered employer's cards come from different plan years.
   */
  planYearsById: Record<string, any>;
}

const initialState = {
  isLoggedIn: false,
  currentEmployee: null,
  view: PLAN_VIEW.ON_BOARDING.type,
  publishedPlans: null,
  electionData: null,
  electionMDVIdCardData: [],
  employerData: null,
  currentPlanYear: {},
  planYearsById: {},
} as IDCardState;

const idCardSlice = createSlice({
  name: 'idCard',
  initialState,
  reducers: {
    setEmployee: (state, { payload }) => {
      state.currentEmployee = payload;
      state.isLoggedIn = true;
    },
    clearEmployee: (state) => {
      state.currentEmployee = null;
      state.isLoggedIn = false;
    },
    setOnboardingView: (state, { payload }) => {
      state.view = payload;
    },
    setInProgress: (state, { payload }) => {
      state.inProgress = payload;
    },
    setPublishedPlans: (state, { payload }) => {
      state.publishedPlans = payload;
    },
    setElectionData: (state, { payload }) => {
      state.electionData = payload;
    },
    setIDCardsData: (state, { payload }) => {
      state.electionMDVIdCardData = payload;
    },
    setDependentIdCardsData: (
      state,
      { payload }: PayloadAction<IDCardState['dependentMDVIdCardData']>
    ) => {
      state.dependentMDVIdCardData = payload;
    },
    setCurrentMemberId: (
      state,
      { payload }: PayloadAction<IDCardState['currentMemberId']>
    ) => {
      state.currentMemberId = payload;
    },
    setEmployerData: (state, { payload }) => {
      state.employerData = payload;
    },
    setPlanYearData: (state, { payload }) => {
      state.currentPlanYear = payload;
    },
    addPlanYears: (state, { payload }: PayloadAction<Record<string, any>>) => {
      state.planYearsById = { ...(state.planYearsById || {}), ...payload };
    },
    resetIDcardState: (state) => {
      Object.assign(state, initialState);
    },
  },
});

export const {
  setEmployee,
  clearEmployee,
  setOnboardingView,
  setPublishedPlans,
  setInProgress,
  setElectionData,
  setIDCardsData,
  setEmployerData,
  setPlanYearData,
  setDependentIdCardsData,
  setCurrentMemberId,
  addPlanYears,
  resetIDcardState,
} = idCardSlice.actions;

// Thunk action to handle ID card login with system message
export const loginWithIDCard = (
  email?: string
): ThunkAction<void, RootState, unknown, any> => {
  return async (dispatch: Dispatch) => {
    // Create and dispatch system message
    const systemMessage: Omit<
      ChatMessage,
      'id' | 'timestamp' | 'createdAt' | 'updatedAt'
    > = {
      sender: Sender.SYSTEM,
      content: ID_CARD_LOGIN_SYSTEM_MESSAGE + (email ? email : '') + '.',
      status: MessageStatus.COMPLETED,
    };

    dispatch(addMessage({ ...systemMessage } as any));
  };
};

// Thunk action to handle ID card logout with system message
export const logoutWithIDCard = (): ThunkAction<
  void,
  RootState,
  unknown,
  any
> => {
  return async (dispatch: Dispatch) => {
    // Clear employee data
    dispatch(clearEmployee());

    // Create and dispatch system message
    const systemMessage: Omit<
      ChatMessage,
      'id' | 'timestamp' | 'createdAt' | 'updatedAt'
    > = {
      sender: Sender.SYSTEM,
      content: ID_CARD_LOGOUT_SYSTEM_MESSAGE,
      status: MessageStatus.COMPLETED,
    };

    dispatch(addMessage({ ...systemMessage } as any));
  };
};

export default idCardSlice.reducer;

export function getPublishedPlans(employeeId: string) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setInProgress(true));
      const response = await PlanService.publishedPlansRequest(employeeId);
      if (response?.status === 200) {
        dispatch(setPublishedPlans(response?.data));
      }
      return response;
    } catch (error: any) {
      const errorResponse = error?.response?.data;
      console.log(errorResponse);
    } finally {
      dispatch(setInProgress(false));
    }
  };
}

// Loads the plan year of every card whose plan year is not the guide's (already loaded as currentPlanYear), so a
// staggered employer's July dental card shows July dates. A plan year that cannot be read is left out and its card
// keeps showing the guide's dates; with one plan year nothing is requested.
const loadCardPlanYears = async (
  dispatch: Dispatch,
  employerId: string | undefined,
  guidePlanYearId: string,
  elections: any[] | undefined
) => {
  if (!employerId) return;
  const ids = Array.from(
    new Set(
      (elections || [])
        .map((e: any) => e?.planYearId)
        .filter((id: any) => id && id !== guidePlanYearId)
    )
  ) as string[];
  await Promise.all(
    ids.map(async (id) => {
      try {
        const res = await PlanService.getCurrentPlanYearData(employerId, id);
        if (res?.status === 200) {
          dispatch(addPlanYears({ [id]: res.data }));
        }
      } catch (error) {
        // keep the guide's dates for this card
      }
    })
  );
};

export function getIDCardData(
  individualId: string,
  employerId: string,
  planYearId: string
) {
  return async (dispatch: Dispatch, getState: any) => {
    const state = getState();
    const language = state.benguide.data?.language || 'ENGLISH';
    try {
      dispatch(setInProgress(true));
      const response = await PlanService.getElectionsData(
        individualId,
        planYearId
      );

      const planYearRes = await PlanService.getCurrentPlanYearData(
        employerId,
        planYearId
      );

      if (response?.status === 200) {
        dispatch(setIDCardsData(response?.data));
      }
      if (planYearRes.status === 200) {
        dispatch(setPlanYearData(planYearRes?.data));
        dispatch(addPlanYears({ [planYearId]: planYearRes?.data }));
      }
      if (response?.status === 200) {
        await loadCardPlanYears(
          dispatch,
          employerId,
          planYearId,
          response?.data
        );
      }
      return response;
    } catch (error: any) {
      const errorResponse = error?.response?.data;
      handleErrorCode(dispatch, errorResponse, language || 'ENGLISH');
    } finally {
      dispatch(setInProgress(false));
    }
  };
}

export const getDependentIdCardData = (): ThunkAction<
  void,
  RootState,
  unknown,
  any
> => {
  return async (dispatch, getState) => {
    const {
      benguide: {
        data: { planYearId, language },
      },
      employeeAuth: { dependentDetails },
    } = getState();
    try {
      dispatch(setInProgress(true));
      const allResponses = await Promise.all(
        dependentDetails?.map(
          async (d: any) => await PlanService.getElectionsData(d.id, planYearId)
        )
      );
      const mappedData: IDCardState['dependentMDVIdCardData'] = {};
      allResponses.forEach(({ data }) => {
        const individualId: string = data?.[0]?.individualId;
        mappedData[individualId] = data;
      });
      dispatch(setDependentIdCardsData(mappedData));
      await loadCardPlanYears(
        dispatch,
        getState().idCard.currentEmployee?.employerId,
        planYearId,
        allResponses.flatMap(({ data }) => data || [])
      );
      return allResponses;
    } catch (error: any) {
      const errorResponse = error?.response?.data;
      handleErrorCode(dispatch, errorResponse, language || 'ENGLISH');
    } finally {
      dispatch(setInProgress(false));
    }
  };
};

export function getEmployerData(employerId: string) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setInProgress(true));
      const response = await PlanService.getEmployerDetails(employerId);
      if (response?.status === 200) {
        dispatch(setEmployerData(response?.data));
      }
      return response;
    } catch (error: any) {
      const errorResponse = error?.response?.data;
      console.log(errorResponse);
    } finally {
      dispatch(setInProgress(false));
    }
  };
}

export function hasElections(individualId: string, planYearId: string) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setInProgress(true));
      const response = await PlanService.hasElections(individualId, planYearId);
      if (response?.status === 200) {
        dispatch(setElectionData(response?.data));
        if (response?.data?.hasElection) {
          dispatch(setOnboardingView(PLAN_VIEW.CARD.type));
        } else {
          dispatch(setOnboardingView(PLAN_VIEW.ON_BOARDING.type));
        }
      }
      return response;
    } catch (error: any) {
      const errorResponse = error?.response?.data;
      console.log(errorResponse);
    } finally {
      dispatch(setInProgress(false));
    }
  };
}

export function saveElections(
  individualId: string,
  employerId: string,
  planYearId: string,
  plans: any
) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setInProgress(true));
      const response = await PlanService.saveElections(
        individualId,
        employerId,
        planYearId,
        plans
      );
      if (response?.status === 200) {
        dispatch(setPublishedPlans(response?.data));
      }
      return response;
    } catch (error: any) {
      const errorResponse = error?.response?.data;
      console.log(errorResponse);
    } finally {
      dispatch(setInProgress(false));
    }
  };
}

export function updateMemberId(
  individualId: string,
  planYearId: string,
  benefitKind: string,
  memberId: string
) {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setInProgress(true));
      const response = await PlanService.updateMemberId(
        individualId,
        planYearId,
        benefitKind,
        memberId
      );
      if (response?.status === 204) {
        const response = await PlanService.getElectionsData(
          individualId,
          planYearId
        );
        if (response?.status === 200) {
          dispatch(setIDCardsData(response?.data));
        }
      }
      return response;
    } catch (error: any) {
      const errorResponse = error?.response?.data;
      console.log(errorResponse);
    } finally {
      dispatch(setInProgress(false));
    }
  };
}
